import React, { useState } from 'react';

const DEFAULT_API_URL = 'https://mizar-api.vercel.app';

function getStatusLabel(status) {
  if (status >= 200 && status < 300) return `${status} OK`;
  if (status === 401) return '401 Unauthorized';
  if (status === 403) return '403 Forbidden';
  if (status === 404) return '404 Not Found';
  if (status >= 500) return `${status} Server Error`;
  return `${status}`;
}

export default function CSVReportDownload() {
  const [apiUrl, setApiUrl] = useState(DEFAULT_API_URL);
  const [apiKey, setApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(false);
  const [latency, setLatency] = useState(null);
  const [error, setError] = useState(null);
  const [downloaded, setDownloaded] = useState(false);
  const [copied, setCopied] = useState(false);

  const canDownload = Boolean(apiKey.trim()) && Boolean(startDate) && Boolean(endDate) && !loading;
  const reportUrl = `${apiUrl}/v1/reports/decisions.csv?start_date=${startDate}&end_date=${endDate}`;

  const copyCurl = async () => {
    const key = apiKey || 'YOUR_API_KEY';
    const snippet = `curl -L \\
  -H "Authorization: Bearer ${key}" \\
  ${apiUrl}/v1/reports/decisions.csv \\
  -o mizar_decisions.csv`;

    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const downloadCsv = async () => {
    setLoading(true);
    setError(null);
    setDownloaded(false);
    setLatency(null);

    const start = performance.now();

    try {
      if (startDate > endDate) {
        throw new Error('Start date cannot be after end date.');
      }

      const res = await fetch(reportUrl, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${apiKey.trim()}`,
        },
      });

      const ms = Math.round(performance.now() - start);
      const contentType = res.headers.get('content-type') || '';

      setLatency({
        status: res.status,
        ms,
        ok: res.ok,
        label: getStatusLabel(res.status),
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(`HTTP ${res.status}: ${text || 'CSV export failed.'}`);
      }

      if (!contentType.includes('text/csv')) {
        const text = await res.text();
        throw new Error(`Unexpected response type: ${contentType || 'unknown'}. ${text}`);
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const filename = `mizar_decisions_${startDate}_to_${endDate}.csv`;

      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
      setDownloaded(true);
    } catch (err) {
      setError(err.message || 'CSV export failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        .csv-report-container {
          background: var(--bg-glass);
          border: 1px solid var(--border-subtle);
          border-radius: 12px;
          padding: 32px;
          margin-top: 32px;
        }

        .csv-report-title {
          margin: 0 0 8px 0;
          font-size: 24px;
          font-weight: 700;
        }

        .csv-report-subtitle {
          margin: 0 0 24px 0;
          color: var(--text-secondary);
          font-size: 14px;
          line-height: 1.5;
        }

        .csv-report-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }

        .csv-report-section {
          margin-bottom: 24px;
        }

        .csv-report-label-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 8px;
        }

        .csv-report-label {
          display: block;
          font-weight: 600;
        }

        .csv-report-input {
          width: 100%;
          padding: 12px;
          border-radius: 8px;
          border: 1px solid var(--border-subtle);
          background: var(--bg-primary);
          color: var(--text-primary);
          font-size: 14px;
          box-sizing: border-box;
          font-family: 'Courier New', monospace;
        }

        .csv-report-input:focus {
          outline: none;
          border-color: var(--accent-primary);
        }

        .csv-report-helper {
          margin-top: 8px;
          font-size: 13px;
          color: var(--text-muted);
        }

        .csv-report-button-row {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
        }

        .csv-report-button {
          padding: 12px 32px;
          border-radius: 8px;
          border: none;
          background: var(--accent-primary);
          color: white;
          font-weight: 600;
          cursor: pointer;
          font-size: 16px;
          transition: opacity 0.2s ease;
        }

        .csv-report-button:hover:not(:disabled) {
          opacity: 0.9;
        }

        .csv-report-button:disabled {
          background: #666;
          cursor: not-allowed;
        }

        .csv-secondary-btn,
        .csv-copy-btn {
          padding: 8px 12px;
          border-radius: 6px;
          border: 1px solid var(--border-subtle);
          background: transparent;
          color: var(--text-secondary);
          font-size: 13px;
          cursor: pointer;
        }

        .csv-secondary-btn:hover,
        .csv-copy-btn:hover {
          border-color: var(--accent-primary);
          color: var(--accent-primary);
        }

        .csv-copy-btn.copied {
          border-color: #5FD4A0;
          color: #5FD4A0;
        }

        .csv-report-status {
          margin-top: 24px;
          font-family: 'Courier New', monospace;
          font-size: 13px;
        }

        .csv-status-success {
          color: #5FD4A0;
          font-weight: 700;
        }

        .csv-status-error {
          color: #EF6B6B;
          font-weight: 700;
        }

        .csv-status-muted {
          color: var(--text-muted);
        }

        .csv-error-box {
          margin-top: 12px;
          padding: 16px;
          border-radius: 8px;
          background: rgba(239, 107, 107, 0.08);
          border: 1px solid rgba(239, 107, 107, 0.45);
          color: #EF6B6B;
          white-space: pre-wrap;
          word-break: break-word;
        }

        @media (max-width: 640px) {
          .csv-report-container {
            padding: 20px;
          }

          .csv-report-button,
          .csv-copy-btn,
          .csv-secondary-btn {
            width: 100%;
          }

          .csv-report-grid {
            grid-template-columns: 1fr;
            gap: 0;
          }

          .csv-report-button-row {
            align-items: stretch;
          }

          .csv-report-label-row {
            flex-direction: column;
            align-items: stretch;
          }
        }
      `}</style>

      <div className="csv-report-container">
        <h3 className="csv-report-title">CSV Report Download</h3>

        <p className="csv-report-subtitle">
          Download your authenticated MIZAR decision report as a CSV file.
        </p>


        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '16px',
            marginBottom: '24px'
          }}
        >
          <div>
            <label className="csv-report-label">Start date</label>

            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="csv-report-input"
            />
          </div>

          <div>
            <label className="csv-report-label">End date</label>

            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="csv-report-input"
            />
          </div>
        </div>

        <div className="csv-report-section">
          <label className="csv-report-label">API URL</label>
          <input
            type="text"
            value={apiUrl}
            onChange={(e) => setApiUrl(e.target.value)}
            className="csv-report-input"
            placeholder={DEFAULT_API_URL}
            autoComplete="off"
            spellCheck={false}
          />
        </div>

        <div className="csv-report-section">
          <div className="csv-report-label-row">
            <label className="csv-report-label">API Key</label>
            <button
              type="button"
              className="csv-secondary-btn"
              onClick={() => setShowApiKey((prev) => !prev)}
            >
              {showApiKey ? 'Hide' : 'Show'}
            </button>
          </div>

          <input
            type={showApiKey ? 'text' : 'password'}
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            className="csv-report-input"
            placeholder="Enter your API key"
            autoComplete="off"
            spellCheck={false}
          />

          <div className="csv-report-helper">
            Uses the same Bearer token as the live prediction API.
          </div>
        </div>

        <div className="csv-report-button-row">
          <button
            type="button"
            onClick={downloadCsv}
            disabled={!canDownload}
            className="csv-report-button"
          >
            {loading ? 'Downloading...' : 'Download CSV'}
          </button>

          <button
            type="button"
            onClick={copyCurl}
            className={`csv-copy-btn${copied ? ' copied' : ''}`}
          >
            {copied ? '✓ Copied' : 'Copy cURL'}
          </button>
        </div>

        {(latency || downloaded || error) && (
          <div className="csv-report-status">
            {latency && (
              <div>
                <span className={latency.ok ? 'csv-status-success' : 'csv-status-error'}>
                  {latency.label}
                </span>
                <span className="csv-status-muted"> • {latency.ms}ms</span>
              </div>
            )}

            {downloaded && (
              <div className="csv-status-success" style={{ marginTop: '8px' }}>
                CSV downloaded successfully.
              </div>
            )}

            {error && <div className="csv-error-box">{error}</div>}
          </div>
        )}
      </div>
    </>
  );
}
